import {
  ContributorCard,
  MusicCard,
  SerenityCard,
  WisdomCard,
} from '@components/Cards';
import NapsterSpinner from '@components/NapsterSpinner';
import useAlbumData from '@hooks/useAlbumData';
import { useStationMode } from '@hooks/station';
import { sendSelectCompanionMessage } from '@utils/station';
import { FeatureItem } from 'src/types';

interface FeaturedSlideProps {
  slide: FeatureItem;
  index: number;
  onPlayNow: (slide: FeatureItem) => void;
  isLoading?: boolean;
  isCarouselItem?: boolean; // Flag to identify static carousel items
}

export const FeaturedSlide = ({
  slide,
  index,
  onPlayNow,
  isLoading = false,
}: FeaturedSlideProps) => {
  const { addFavouriteItem } = useAlbumData();
  const isStationMode = useStationMode();

  const videoDesktop = slide.previewVideoUrl || '';
  const image = slide.previewImageUrl;
  const itemType = slide.type.toLowerCase();
  const contributor = slide.contributors?.[0];

  // Priority for poster image: slide.videoMobilePreviewUrl -> contributor?.videoMobilePreviewUrl -> slide.previewImageUrl
  const posterUrl =
    slide.videoMobilePreviewUrl || contributor?.videoMobilePreviewUrl || image;

  // Determine card type based on category and type
  const isMusic =
    (slide.category === 'music' || slide.category === 'Indian music') &&
    slide.type === 'Album';
  const isContributor = slide.category === null && slide.type === 'Contributor';
  const isBook = slide.category === 'book' && slide.type === 'Album';
  const isSerenity = slide.category === 'serenity' && slide.type === 'Album';
  const isCreation =
    slide.category === 'my-creations' && slide.type === 'Creation';

  const handleSlideClick = () => {
    if (isContributor && isStationMode) {
      sendSelectCompanionMessage(slide.id);
      return;
    }
    onPlayNow(slide);
  };

  return (
    <div
      key={`Slide-${index}`}
      className={`flex-shrink-0 snap-start relative rounded-[16px] shadow-[0_4px_16px_0_rgba(0,0,0,0.40)] cursor-pointer hover:shadow-[0_4px_20px_0_rgba(0,0,0,0.50)] transition-all duration-300 opacity-100 ${
        isStationMode ? 'w-[282px] h-[395px]' : 'w-[371px] h-[520px]'
      }`}
      onClick={handleSlideClick}>
      {/* MUSIC CARD DESIGN */}
      {isMusic && (
        <MusicCard
          album={slide}
          contributor={contributor}
          videoUrl={videoDesktop}
          posterUrl={posterUrl || ''}
          albumArtUrl={slide.albumArtUrl}
          albumName={slide.albumName || slide.name}
          likes={slide.likes}
          isLiked={slide.isLiked}
          onClick={handleSlideClick}
          onFavoriteClick={(e) => {
            e.stopPropagation();
            addFavouriteItem(itemType, slide.id, slide.isLiked);
          }}
          showPlayButton={false}
          showFavoriteButton={false}
          showLikesCount={true}
          compactSize={isStationMode}
        />
      )}

      {/* CONTRIBUTOR/COMPANION CARD DESIGN */}
      {isContributor && (
        <ContributorCard
          contributor={slide}
          videoUrl={videoDesktop}
          posterUrl={posterUrl || undefined}
          name={slide.name}
          callToAction={slide.call_to_action}
          firstName={slide.firstName}
          lastName={slide.lastName}
          jobTitle={slide.jobTitle}
          onClick={handleSlideClick}
          onFavoriteClick={(e) => {
            e.stopPropagation();
            addFavouriteItem(itemType, slide.id, slide.isLiked);
          }}
          showFavoriteButton={false}
          isLoading={isLoading}
          isFavorited={slide.isLiked}
        />
      )}

      {/* BOOK/AUDIOBOOK CARD DESIGN - Using WisdomCard component */}
      {isBook && (
        <WisdomCard
          album={slide}
          contributor={contributor}
          albumArtUrl={slide.albumArtUrl || image}
          albumName={slide.albumName || slide.name}
          likes={slide.likes}
          isLiked={slide.isLiked}
          onClick={handleSlideClick}
          onFavoriteClick={(e) => {
            e.stopPropagation();
            addFavouriteItem(itemType, slide.id, slide.isLiked);
          }}
          showLikesCount={true}
          compactSize={isStationMode}
        />
      )}

      {/* SERENITY CARD DESIGN - Using SerenityCard component */}
      {isSerenity && (
        <SerenityCard
          album={slide}
          contributor={contributor}
          videoUrl={videoDesktop}
          posterUrl={posterUrl}
          albumName={slide.albumName || slide.name}
          likes={slide.likes}
          isLiked={slide.isLiked}
          onClick={handleSlideClick}
          onFavoriteClick={(e) => {
            e.stopPropagation();
            addFavouriteItem(itemType, slide.id, slide.isLiked);
          }}
          showLikesCount={true}
          compactSize={isStationMode}
        />
      )}

      {/* CREATION CARD DESIGN - Using MusicCard component for user creations */}
      {isCreation && (
        <MusicCard
          album={slide}
          contributor={contributor}
          videoUrl={videoDesktop}
          posterUrl={posterUrl || ''}
          albumArtUrl={slide.albumArtUrl}
          albumName={slide.albumName || slide.name}
          likes={slide.likes}
          isLiked={slide.isLiked}
          onClick={handleSlideClick}
          showPlayButton={false}
          showFavoriteButton={false}
          showLikesCount={false}
          compactSize={isStationMode}
        />
      )}

      {isLoading && (
        <div className="absolute z-[100] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <NapsterSpinner />
        </div>
      )}
    </div>
  );
};
